/**
 * Team Report Generator Utility
 * Generates team report HTML from template with radar charts for all members
 *
 * Requirements 25-32: Network graph page with similarity-based connections
 */

import {
  findPatternByCode,
  type TeamJudgmentPattern,
} from '../../admin/api/teamJudgmentPatternApi';
import {
  generateNetworkGraph,
  generateNetworkSvg,
  type NetworkGraphOptions,
} from './networkGraphGenerator';
import { ORG_REPORT_NOTES, CONNECTION_INTRO_COPY } from './orgReportCopy';

export interface MemberScore {
  name: string;
  scores: {
    A: number;
    B: number;
    C: number;
    D: number;
  };
}

/**
 * Team type counts for determining TTYPE
 */
interface TeamTypeCounts {
  CCCNT: number; // All 4 elements <= 14 (7 after /2)
  RCCNT: number; // XVALUE positive AND C > D
  RDCNT: number; // XVALUE positive AND C < D
  LACNT: number; // XVALUE negative AND A > B
  LBCNT: number; // XVALUE negative AND A < B
  TACNT: number; // YVALUE negative AND A > D
  TDCNT: number; // YVALUE negative AND A < D
  BBCNT: number; // YVALUE positive AND B > C
  BCCNT: number; // YVALUE positive AND B < C
}

/**
 * Team description fields from TeamJudgmentPattern
 */
interface TeamDescriptions {
  tdesc11: string;
  tdesc12: string;
  tdesc13: string;
  tdesc14: string;
  tdesc15: string;
  tdesc16: string;
  tdesc17: string;
  tdesc18: string;
  tdesc19: string;
}

/**
 * Team description fields for point page (tdesc21-27)
 */
interface TeamDescriptions2x {
  tdesc21: string;
  tdesc22: string;
  tdesc23: string;
  tdesc24: string;
  tdesc25: string;
  tdesc26: string;
  tdesc27: string;
}

/**
 * Calculate team type counts from member scores
 * Note: scores are already divided by 2 (max 12), so threshold 14 becomes 7
 */
function calculateTeamTypeCounts(members: MemberScore[]): TeamTypeCounts {
  const counts: TeamTypeCounts = {
    CCCNT: 0,
    RCCNT: 0,
    RDCNT: 0,
    LACNT: 0,
    LBCNT: 0,
    TACNT: 0,
    TDCNT: 0,
    BBCNT: 0,
    BCCNT: 0,
  };

  for (const member of members) {
    const { A, B, C, D } = member.scores;

    // XVALUE = ((C + D) - (A + B)) / 1.5
    const xValue = (C + D - (A + B)) / 1.5;
    // YVALUE = ((A + D) - (B + C)) * -1 / 1.5
    const yValue = ((A + D - (B + C)) * -1) / 1.5;

    // 11. All 4 elements <= 14 (scores are /2, so check <= 7)
    if (A <= 7 && B <= 7 && C <= 7 && D <= 7) {
      counts.CCCNT++;
    }

    // 12. XVALUE positive AND C > D
    if (xValue > 0 && C > D) {
      counts.RCCNT++;
    }

    // 13. XVALUE positive AND C < D
    if (xValue > 0 && C < D) {
      counts.RDCNT++;
    }

    // 14. XVALUE negative AND A > B
    if (xValue < 0 && A > B) {
      counts.LACNT++;
    }

    // 15. XVALUE negative AND A < B
    if (xValue < 0 && A < B) {
      counts.LBCNT++;
    }

    // 16. YVALUE negative AND A > D
    if (yValue < 0 && A > D) {
      counts.TACNT++;
    }

    // 17. YVALUE negative AND A < D
    if (yValue < 0 && A < D) {
      counts.TDCNT++;
    }

    // 18. YVALUE positive AND B > C
    if (yValue > 0 && B > C) {
      counts.BBCNT++;
    }

    // 19. YVALUE positive AND B < C
    if (yValue > 0 && B < C) {
      counts.BCCNT++;
    }
  }

  return counts;
}

/**
 * Determine TTYPE from team type counts
 * 20. CCCNT >= 4 → TTYPE = "CC"
 * 21. CCCNT < 4 → TTYPE = "EE"
 * 22. Find max of RCCNT,RDCNT,LACNT,LBCNT,TACNT,TDCNT,BBCNT,BCCNT and use first 2 chars
 */
function determineTType(counts: TeamTypeCounts): string {
  // 20-21: Check CCCNT first
  if (counts.CCCNT >= 4) {
    return 'CC';
  }

  // 22: Find max among the 8 counts
  const countMap: Record<string, number> = {
    RC: counts.RCCNT,
    RD: counts.RDCNT,
    LA: counts.LACNT,
    LB: counts.LBCNT,
    TA: counts.TACNT,
    TD: counts.TDCNT,
    BB: counts.BBCNT,
    BC: counts.BCCNT,
  };

  let maxKey = 'EE';
  let maxValue = 0;

  for (const [key, value] of Object.entries(countMap)) {
    if (value > maxValue) {
      maxValue = value;
      maxKey = key;
    }
  }

  // If all counts are 0, return EE
  return maxValue > 0 ? maxKey : 'EE';
}

/**
 * Team distribution counts for Step 1-2
 */
interface TeamDistributionCounts {
  cntA: number;
  cntB: number;
  cntC: number;
  cntD: number;
}

/**
 * Distribution judgment result
 */
interface DistributionJudgment {
  bttype: string | null; // とりわけ大きい要素のタイプ
  sttype: string | null; // とりわけ小さい要素のタイプ
}

/**
 * Calculate team distribution counts (Step 1-2)
 * vl = (A + B) - (C + D)
 * vt = (A + D) - (B + C)
 */
function calculateTeamDistribution(members: MemberScore[]): TeamDistributionCounts {
  let cntA = 0;
  let cntB = 0;
  let cntC = 0;
  let cntD = 0;

  for (const member of members) {
    const { A, B, C, D } = member.scores;

    // Step 1: Calculate vl and vt
    const vl = A + B - (C + D);
    const vt = A + D - (B + C);

    // Step 2.1-2.4: Count based on vl and vt
    if (vl > 0 && vt > 0) {
      cntA++;
    } else if (vl > 0 && vt < 0) {
      cntB++;
    } else if (vl < 0 && vt < 0) {
      cntC++;
    } else if (vl < 0 && vt > 0) {
      cntD++;
    } else {
      // Step 2.5: Handle edge cases when vl or vt is 0
      // Step 2.5.1: If both vl and vt are 0, do nothing
      if (vl === 0 && vt === 0) {
        continue;
      }

      // Step 2.5.2-2.5.5: Add 0.5 based on non-zero values
      if (vl > 0) {
        cntA += 0.5;
        cntB += 0.5;
      }
      if (vl < 0) {
        cntC += 0.5;
        cntD += 0.5;
      }
      if (vt > 0) {
        cntA += 0.5;
        cntD += 0.5;
      }
      if (vt < 0) {
        cntB += 0.5;
        cntC += 0.5;
      }
    }
  }

  return { cntA, cntB, cntC, cntD };
}

/**
 * Judge distribution ratio (Step 3-4, 6)
 * Compare with reference matrix A = [[6.3, 7.6], [4.4, 5.7]]
 * Matrix B = [[cntA, cntD], [cntB, cntC]]
 */
function judgeDistributionRatio(counts: TeamDistributionCounts): DistributionJudgment {
  const { cntA, cntB, cntC, cntD } = counts;

  // Reference matrix A (industry average)
  const matrixA = [
    [6.3, 7.6],
    [4.4, 5.7],
  ];

  // Team matrix B
  const matrixB = [
    [cntA, cntD],
    [cntB, cntC],
  ];

  // Calculate sums for normalization
  const sumA = matrixA[0][0] + matrixA[0][1] + matrixA[1][0] + matrixA[1][1];
  const sumB = matrixB[0][0] + matrixB[0][1] + matrixB[1][0] + matrixB[1][1];

  // Avoid division by zero
  if (sumB === 0) {
    return { bttype: null, sttype: null };
  }

  // Normalize matrices (構成比)
  const PA = [
    [matrixA[0][0] / sumA, matrixA[0][1] / sumA],
    [matrixA[1][0] / sumA, matrixA[1][1] / sumA],
  ];
  const PB = [
    [matrixB[0][0] / sumB, matrixB[0][1] / sumB],
    [matrixB[1][0] / sumB, matrixB[1][1] / sumB],
  ];

  // Calculate ratio (PA / PB)
  const ratio = [
    [PB[0][0] > 0 ? PA[0][0] / PB[0][0] : 0, PB[0][1] > 0 ? PA[0][1] / PB[0][1] : 0],
    [PB[1][0] > 0 ? PA[1][0] / PB[1][0] : 0, PB[1][1] > 0 ? PA[1][1] / PB[1][1] : 0],
  ];

  // Thresholds
  const TH_LARGE = 1.3;
  const TH_SMALL = 0.75;

  // Position to TTYPE mapping
  // (1,1) = TA, (1,2) = TD, (2,1) = BB, (2,2) = BC
  const positionToType: Record<string, string> = {
    '0,0': 'TA',
    '0,1': 'TD',
    '1,0': 'BB',
    '1,1': 'BC',
  };

  let bttype: string | null = null;
  let sttype: string | null = null;

  // Find とりわけ大きい and とりわけ小さい
  for (let i = 0; i < 2; i++) {
    for (let j = 0; j < 2; j++) {
      const r = ratio[i][j];
      const key = `${i},${j}`;

      // Note: ratio = PA/PB, so if ratio is small, PB is relatively large
      // とりわけ大きい: ratio <= TH_SMALL means team has more of this element
      if (r <= TH_SMALL && r > 0 && !bttype) {
        bttype = positionToType[key];
      }
      // とりわけ小さい: ratio >= TH_LARGE means team has less of this element
      if (r >= TH_LARGE && !sttype) {
        sttype = positionToType[key];
      }
    }
  }

  console.log('=== Distribution Judgment ===');
  console.log('Counts:', counts);
  console.log('PA:', PA);
  console.log('PB:', PB);
  console.log('Ratio:', ratio);
  console.log('BTTYPE (とりわけ大きい):', bttype);
  console.log('STTYPE (とりわけ小さい):', sttype);
  console.log('=============================');

  return { bttype, sttype };
}

/**
 * Get team descriptions from pattern or return defaults
 */
function getTeamDescriptions(pattern: TeamJudgmentPattern | null): TeamDescriptions {
  if (!pattern) {
    return {
      tdesc11: '組織の特性を分析中',
      tdesc12: '詳細な分析結果は準備中です。',
      tdesc13: '課題の特定中',
      tdesc14: '詳細な課題分析は準備中です。',
      tdesc15: '組織構造の分析中',
      tdesc16: '詳細な構造分析は準備中です。',
      tdesc17: '負荷分析中',
      tdesc18: '詳細な負荷分析は準備中です。',
      tdesc19: '組織の特性を分析中です',
    };
  }

  return {
    tdesc11: pattern.tdesc11 || '',
    tdesc12: pattern.tdesc12 || '',
    tdesc13: pattern.tdesc13 || '',
    tdesc14: pattern.tdesc14 || '',
    tdesc15: pattern.tdesc15 || '',
    tdesc16: pattern.tdesc16 || '',
    tdesc17: pattern.tdesc17 || '',
    tdesc18: pattern.tdesc18 || '',
    tdesc19: pattern.tdesc19 || '',
  };
}

export interface TeamReportData {
  companyName: string;
  date: string;
  members: MemberScore[];
  networkOptions?: NetworkGraphOptions; // Optional network graph options
  adjustedNodePositions?: { [id: number]: { x: number; y: number } }; // Manual node position adjustments
  /**
   * 部署別「つながりの構造」ページの元データ。
   * 指定時は部署ごとに1ページ生成する（未指定なら従来どおり全社1ページのみ）。
   * 分析結果のある社員が2名未満の部署はネットワーク図を描けないため、
   * HTML には理由を表示しつつ data-pdf-skip を付け PDF からは除外する。
   */
  departments?: Array<{ name: string; members: MemberScore[] }>;
  /**
   * 「つながりの構造」= 相談ネットワーク（Web版§03と同じデータ）。
   * 指定時はこちらを使い、思考タイプ類似度のネットワーク図は出力しない。
   */
  consultDepts?: ConsultDeptStat[];
  connectionLead?: string;
  /**
   * 組織スコア/タイプ（Web版 orgScore.ts の算出結果）。
   * 指定時のみ「1 組織の評価」セクションを出力する。
   */
  orgEvaluation?: OrgEvaluationInput;
}

/** ネットワーク図を描くのに必要な最小人数（2名未満は線が引けない）。 */
export const MIN_NETWORK_MEMBERS = 2;

// レーダーチャート一覧ページのグリッド定義。
// CSS の .c-report-single_radar-list_radars (grid-template-columns: repeat(7, 1fr)) と
// 表示行数(3行) に一致させること。1ページの表示上限 = 列数 × 行数。
// この値を変更する場合は必ず public/report-template/assets/css/style.css の
//   - .c-report-single_radar-list_radars の grid-template-columns（列数）
//   - .c-report-single_radar-list_radars_item:nth-child(n+22) のセーフティネット（= 上限+1）
// も同時に更新すること（片方だけ変えると 22 人目以降が無言で非表示になり崩れる）。
const RADAR_LIST_COLUMNS = 7;
const RADAR_LIST_ROWS = 3;
export const MEMBERS_PER_PAGE = RADAR_LIST_COLUMNS * RADAR_LIST_ROWS; // 21

/**
 * Calculate radar chart path for SVG
 * A=135°, B=225°, C=315°, D=45°
 */
function calculateRadarPath(scores: { A: number; B: number; C: number; D: number }): string {
  const maxVal = 12;
  const center = 75; // 150/2 for viewBox 0 0 150 150
  const maxRadius = 63;

  const getPoint = (angle: number, value: number) => {
    const rad = (angle * Math.PI) / 180;
    const r = (value / maxVal) * maxRadius;
    return {
      x: center + r * Math.cos(rad),
      y: center - r * Math.sin(rad),
    };
  };

  const pA = getPoint(135, scores.A);
  const pB = getPoint(225, scores.B);
  const pC = getPoint(315, scores.C);
  const pD = getPoint(45, scores.D);

  return `M${pA.x} ${pA.y} L${pB.x} ${pB.y} L${pC.x} ${pC.y} L${pD.x} ${pD.y} Z`;
}

/**
 * Generate radar chart item HTML for a single member
 */
export function generateRadarItem(member: MemberScore): string {
  const path = calculateRadarPath(member.scores);
  return `
    <li class="c-report-single_radar-list_radars_item">
      <p class="c-report-single_radar-list_radars_name">${escapeHtml(member.name)}</p>
      <div class="c-report-single_radar-list_radars_box">
        <svg class="js-radar-chart" data-a="${member.scores.A}" data-b="${member.scores.B}" data-c="${member.scores.C}" data-d="${member.scores.D}" viewBox="0 0 150 150">
          <path class="js-radar-chart_line" d="${path}"></path>
        </svg>
      </div>
    </li>`;
}

/**
 * Generate table of contents page (Requirement 39)
 */
/** 目次の1項目（節番号・見出し・ページ番号・任意の補足文）。 */
export interface TocEntry {
  no: string;
  title: string;
  page: number;
  note?: string;
}

/** 目次の1セクション。 */
export interface TocSection {
  title: string;
  entries: TocEntry[];
}

/**
 * 目次ページ。
 * 本文の採番（generateTeamReportHtml のページ採番）から組み立てるため、
 * ページを増減しても目次と本文のページ番号がずれない。
 */
export function generateTocPage(sections: TocSection[]): string {
  const list = sections
    .filter((sec) => sec.entries.length > 0)
    .map(
      (sec) => `
          <li class="c-report-single_toc_list_item">
            <h4 class="c-report-single_toc_list_ttl">${escapeHtml(sec.title)}</h4>
            <ol class="c-report-single_toc_list_child">
              ${sec.entries
                .map(
                  (e) => `
              <li class="c-report-single_toc_list_child_item">
                <div class="c-report-single_toc_list_child_item_inner">
                  <h5 class="c-report-single_toc_list_child_ttl">${escapeHtml(e.no)} ${escapeHtml(e.title)}</h5>
                  <p class="c-report-single_toc_list_child_num">${String(e.page).padStart(2, '0')}</p>
                </div>
                ${e.note ? `<p class="c-report-single_toc_list_child_txt">${escapeHtml(e.note)}</p>` : ''}
              </li>`
                )
                .join('')}
            </ol>
          </li>`
    )
    .join('');

  return `
    <li class="c-report-single_list_item">
      <div class="c-report-single_toc">
        <hgroup class="c-report-single_toc_hgroup">
          <h3 class="c-report-single_toc_hgroup_ttl">目次</h3>
          <p class="c-report-single_toc_hgroup_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
        </hgroup>

        <ol class="c-report-single_toc_list">${list}
        </ol>

        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">01</p>
        </footer>
      </div>
    </li>`;
}

/**
 * Generate overview radar figure for distribution chart (Requirement 46: transparent background)
 * XVALUE = ((C + D) - (A + B)) / 1.5
 * YVALUE = ((A + D) - (B + C)) * -1 / 1.5
 */
function generateOverviewRadarFigure(member: MemberScore): string {
  const { A, B, C, D } = member.scores;
  const xValue = (C + D - (A + B)) / 1.5;
  const yValue = ((A + D - (B + C)) * -1) / 1.5;
  const path = calculateRadarPath(member.scores);

  // Requirement 46: Use transparent background for radar charts in distribution view
  return `
    <figure class="c-report-single_radar-overview_box_radar" title="${escapeHtml(member.name)}" style="--_radar-pos-x-val: ${xValue}; --_radar-pos-y-val: ${yValue};">
      <svg class="js-radar-chart" data-a="${A}" data-b="${B}" data-c="${C}" data-d="${D}" viewBox="0 0 150 150" style="background: transparent;">
        <path class="js-radar-chart_line" d="${path}" style="fill: rgba(59, 130, 246, 0.3); stroke: #3B82F6;"></path>
      </svg>
    </figure>`;
}

/**
 * 分布図(散布図)は全メンバーを1ページにスコア座標で配置するため人数分割できない。
 * 人数が多いときはチャート自体を縮小して重なりを緩和する。
 * 返り値は各チャートの倍率(1 以下)。CSS 側で var(--_overview-radar-scale) として使用。
 */
function computeOverviewRadarScale(memberCount: number): number {
  if (memberCount <= 20) return 1;
  if (memberCount <= 40) return 0.8;
  if (memberCount <= 60) return 0.65;
  return 0.5;
}

/**
 * Generate overview page with distribution chart
 * Includes team type analysis and TDESC11-19 replacement
 */
export function generateOverviewPage(
  members: MemberScore[],
  pageNumber: number,
  teamDescriptions: TeamDescriptions,
  footerNumber?: number,
  sectionNumber?: string
): string {
  const radarFigures = members.map((m) => generateOverviewRadarFigure(m)).join('\n');
  const overviewRadarScale = computeOverviewRadarScale(members.length);

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_radar-overview">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${sectionNumber ?? `1-${pageNumber}`}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">組織メンバーのGBタイプ診断結果</h3>
              <p class="c-report-single_header-team_hgroup_txt">レーダーチャート分布図と組織概要</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
          <p class="c-report-single_header-team_txt">分布図が、業務のつながりにどのように影響しているか、次のページから組織の強みや課題を詳しく分析します。</p>
        </header>

        <div class="c-report-single_radar-overview_inner">
          <div class="c-report-single_radar-overview_box" style="--_overview-radar-scale: ${overviewRadarScale};">
            ${radarFigures}

            <p class="c-report-single_radar-overview_box_txt _a">
              <span class="c-report-single_radar-overview_box_txt_lg">A</span>論理
            </p>
            <p class="c-report-single_radar-overview_box_txt _b">
              <span class="c-report-single_radar-overview_box_txt_lg">B</span>秩序
            </p>
            <p class="c-report-single_radar-overview_box_txt _c">
              <span class="c-report-single_radar-overview_box_txt_lg">C</span>共感
            </p>
            <p class="c-report-single_radar-overview_box_txt _d">
              <span class="c-report-single_radar-overview_box_txt_lg">D</span>創造
            </p>
          </div>

          <dl class="c-report-single_radar-overview_dl">
            <div class="c-report-single_radar-overview_dl_item">
              <dt class="c-report-single_radar-overview_dl_theme">${escapeHtml(teamDescriptions.tdesc11)}</dt>
              <dd class="c-report-single_radar-overview_dl_des">${escapeHtml(teamDescriptions.tdesc12)}</dd>
            </div>
            <div class="c-report-single_radar-overview_dl_item">
              <dt class="c-report-single_radar-overview_dl_theme">${escapeHtml(teamDescriptions.tdesc13)}</dt>
              <dd class="c-report-single_radar-overview_dl_des">${escapeHtml(teamDescriptions.tdesc14)}</dd>
            </div>
            <div class="c-report-single_radar-overview_dl_item">
              <dt class="c-report-single_radar-overview_dl_theme">${escapeHtml(teamDescriptions.tdesc15)}</dt>
              <dd class="c-report-single_radar-overview_dl_des">${escapeHtml(teamDescriptions.tdesc16)}</dd>
            </div>
            <div class="c-report-single_radar-overview_dl_item">
              <dt class="c-report-single_radar-overview_dl_theme">${escapeHtml(teamDescriptions.tdesc17)}</dt>
              <dd class="c-report-single_radar-overview_dl_des">${escapeHtml(teamDescriptions.tdesc18)}</dd>
            </div>
          </dl>

          <hgroup class="c-report-single_radar-overview_hgroup">
            <p class="c-report-single_radar-overview_hgroup_txt">あなたの組織は</p>
            <h4 class="c-report-single_radar-overview_hgroup_ttl">${escapeHtml(teamDescriptions.tdesc19)}</h4>
          </hgroup>
        </div>

        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(footerNumber ?? pageNumber + 2).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/**
 * Generate a single page of radar charts
 */
export function generateRadarPage(
  members: MemberScore[],
  pageNumber: number,
  companyName: string,
  footerNumber?: number,
  sectionNumber?: string
): string {
  // 堅牢化: 1ページの人数はグリッド容量(列×行)を超えてはならない。
  // 超えると CSS の nth-child(n+22) で 22 人目以降が無言で非表示になるため、
  // 呼び出し側の分割ロジックのズレを開発時に検知できるよう警告する。
  if (members.length > MEMBERS_PER_PAGE) {
    console.warn(
      `[teamReportGenerator] レーダー一覧ページ ${pageNumber} のメンバー数 ${members.length} が上限 ${MEMBERS_PER_PAGE} を超えています。` +
        `${MEMBERS_PER_PAGE} 人目以降が非表示になります。MEMBERS_PER_PAGE と CSS グリッドの整合を確認してください。`
    );
  }

  const radarItems = members.map((m) => generateRadarItem(m)).join('\n');

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_radar-list">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${sectionNumber ?? `1-${pageNumber}`}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">組織メンバーのGBタイプ診断結果</h3>
              <p class="c-report-single_header-team_hgroup_txt">レーダーチャート一覧</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
          <p class="c-report-single_header-team_txt">一人ひとり、思考の特性は異なります。誰もが自分自身の得意分野を持っており、その多様性が組織の力になります。</p>
        </header>

        <div class="c-report-single_radar-list_inner">
          <h4 class="c-report-single_radar-list_ttl">${escapeHtml(companyName)}</h4>
          <ul class="c-report-single_radar-list_radars" style="grid-template-columns: repeat(${RADAR_LIST_COLUMNS}, 1fr);">
            ${radarItems}
          </ul>
        </div>

        ${noteBlock(ORG_REPORT_NOTES.memberTypes)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(footerNumber ?? pageNumber + 2).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/**
 * Generate network graph page (Requirements 25-32, 37-38)
 * Creates a page with network graph showing member relationships
 * @param members Array of member scores
 * @param pageNumber Page number for footer
 * @param sectionNumber Section number for header
 * @param options Network graph options (edgeTopPercent, similarityThreshold)
 * @param adjustedNodePositions Optional manual node position adjustments
 */
/**
 * ネットワーク図(px座標系 600×480)は全メンバーをノード配置するため人数分割できない。
 * 人数が多いときはノード半径を縮めて重なり・見切れを緩和する。少人数は従来の 12px。
 */
function computeNetworkNodeRadius(memberCount: number): number {
  if (memberCount <= 20) return 12;
  if (memberCount <= 40) return 10;
  if (memberCount <= 60) return 8;
  return 6;
}

/**
 * 部署別「つながりの構造」ページ。
 * 分析結果のある社員が MIN_NETWORK_MEMBERS 未満の部署はネットワーク図を描けないため、
 * HTML では理由を表示し、data-pdf-skip を付けて PDF からは除外する。
 */
/**
 * セクション中扉（番号＋タイトルのみのシンプルなページ）。
 * 例: 「1 組織の評価」「2 つながりの構造」
 */
/** 組織スコア/組織タイプページの入力（Web版 orgScore.ts の算出結果をそのまま渡す）。 */
export interface OrgEvaluationInput {
  /** 総合評価 0〜100 */
  totalScore: number;
  totalGrade: 'A' | 'B' | 'C' | 'D';
  /** 総合評価のリード文 */
  evaluationLead: string;
  /** 働きがい/つながり/離職の兆し などのゲージ */
  gauges: Array<{ label: string; score: number; grade: string }>;
  /** 組織タイプ（コード＋名称＋解説） */
  orgTypeCode?: string;
  orgTypeName?: string;
  orgTypeDesc?: string;
  /** 組織ダイヤモンド 4軸平均（0〜12） */
  axis?: { A: number; B: number; C: number; D: number };
  axisNote?: string;
  /** 社員の分布（0〜1の正規化座標） */
  /** 社員の分布。axis があれば各人のレーダー形で描く */
  scatter?: ScatterMember[];
  distributionLead?: string;
  /** 評価を上げるために */
  improvementLead?: string;
  improvementTarget?: number;
  improvements?: Array<{
    rank: number;
    rankColor: string;
    title: string;
    reason: string;
    action: string;
    effect: string;
  }>;
}

/**
 * ページ下部に置く「この項目の見方」注記。
 * 文言は orgReportCopy.ts のたたき台を編集するだけで差し替えられる。
 * 空文字なら何も描画しない（ページ構成を変えずに個別に消せる）。
 */
export function noteBlock(text: string): string {
  if (!text) return '';
  return `<div style="flex:0 0 auto;padding:0 3.5% 0.6%;box-sizing:border-box;">
      <p style="margin:0;padding:8px 12px;background:#F5F7FA;border-left:3px solid #9BB4CC;border-radius:4px;font-size:10px;line-height:1.75;color:#4A5568;">${escapeHtml(text)}</p>
    </div>`;
}

/** 相談ネットワーク（Web版 §03 つながりの構造）の1部署分データ。 */
export interface ConsultDeptStat {
  dept: string;
  members: Array<{ id: string; name: string; anonLabel: string; dept: string }>;
  edges: Array<{ from: string; to: string }>;
  inDegree: Record<string, number>;
  keypersonIds: string[];
  maxInDegree: number;
  concentration: number;
  shape: 'triangle' | 'vee' | 'spoke';
  summary: string;
}

const CONSULT_SHAPE_META: Record<
  ConsultDeptStat['shape'],
  { label: string; color: string; mark: string }
> = {
  triangle: { label: '複数キーパーソン（多極・支え合い）', color: '#22A06B', mark: '△' },
  vee: { label: 'V字（一極集中・属人化）', color: '#C0392B', mark: 'V' },
  spoke: { label: 'スポーク（相談が薄い）', color: '#B8860B', mark: '─' },
};

/**
 * 部署の相談ネットワーク図（Web版 ConnectionStructureLevelB と同じ配置ルール）。
 * キーパーソンを中心（複数なら内周）に、その他を外周に円配置し、相談を矢印で結ぶ。
 */
/**
 * 円を <path> で描く。
 * <circle> の cx/cy/r は CSS(SVG2ジオメトリプロパティ)やキャプチャ処理の
 * 計算スタイル展開で上書きされ得るが、<path d> は属性でしか表現できないため
 * どのブラウザ・どのキャプチャ経路でも同じ位置・同じ大きさで描かれる。
 */
export function circlePath(cx: number, cy: number, r: number, attrs: string): string {
  const x = (cx - r).toFixed(1);
  return `<path d="M ${x} ${cy.toFixed(1)} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0" ${attrs}/>`;
}

export function generateConsultNetworkSvg(stat: ConsultDeptStat): string {
  const W = 430;
  const H = 420;
  const cx = W / 2;
  const cy = H / 2;
  const R = 155; // 外周(その他メンバー)の配置半径
  const Rin = 70; // キーパーソンが複数のときの内側配置半径
  const meta = CONSULT_SHAPE_META[stat.shape] ?? CONSULT_SHAPE_META.spoke;
  const kpSet = new Set(stat.keypersonIds);
  const kps = stat.members.filter((m) => kpSet.has(m.id));
  const others = stat.members.filter((m) => !kpSet.has(m.id));
  const pos = new Map<string, { x: number; y: number }>();

  if (kps.length === 1) {
    pos.set(kps[0].id, { x: cx, y: cy });
  } else {
    kps.forEach((m, i) => {
      const a = (2 * Math.PI * i) / Math.max(1, kps.length) - Math.PI / 2;
      pos.set(m.id, { x: cx + Rin * Math.cos(a), y: cy + Rin * Math.sin(a) });
    });
  }
  others.forEach((m, i) => {
    const a = (2 * Math.PI * i) / Math.max(1, others.length) - Math.PI / 2;
    pos.set(m.id, { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) });
  });

  // エッジ（Web版 ConnectionStructureLevelB と同じ配色。矢印マーカーは使わない）
  const lines = stat.edges
    .map((e) => {
      const a = pos.get(e.from);
      const b = pos.get(e.to);
      if (!a || !b) return '';
      const toKp = kpSet.has(e.to);
      return `<line x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}" stroke="${toKp ? meta.color : '#C9D2DC'}" stroke-width="${toKp ? 1.8 : 1}" stroke-opacity="${toKp ? 0.8 : 0.5}"/>`;
    })
    .join('');

  // ノード。氏名は円を横切って全文表示し、白フチ(ハロー)で線の上でも読めるようにする。
  const nodes = stat.members
    .map((m) => {
      const p = pos.get(m.id);
      if (!p) return '';
      const isKp = kpSet.has(m.id);
      const r = isKp ? 22 : 13;
      const inDeg = stat.inDegree[m.id] ?? 0;
      return `${circlePath(p.x, p.y, r, `fill="${isKp ? meta.color : '#EEF2F7'}" stroke="${isKp ? meta.color : '#AEBAC6'}" stroke-width="${isKp ? 2 : 1}"`)}
        <text x="${p.x.toFixed(1)}" y="${(p.y + (isKp ? 1 : 4)).toFixed(1)}" text-anchor="middle" font-size="${isKp ? 11 : 10}" font-weight="${isKp ? 700 : 500}" fill="#1F2A37" stroke="#ffffff" stroke-width="3" stroke-linejoin="round" style="paint-order:stroke;">${escapeHtml(m.name || m.anonLabel)}</text>
        ${isKp ? `<text x="${p.x.toFixed(1)}" y="${(p.y - r - 4).toFixed(1)}" text-anchor="middle" font-size="10" font-weight="700" fill="${meta.color}">キーパーソン(${inDeg})</text>` : ''}`;
    })
    .join('');

  return `<svg viewBox="0 0 ${W} ${H}" style="display:block;width:450px;height:440px;max-width:100%;margin:0 auto;" preserveAspectRatio="xMidYMid meet">
      ${lines}${nodes}
    </svg>`;
}

/**
 * 「つながりの構造とは」解説ページ（セクション2の冒頭）。
 * 部署ごとの図を見る前に、記号の意味と3つの型を1ページで説明する。
 * 3つの型の図は実データ用と同じ描画関数を使うため、凡例と本編の見た目が必ず一致する。
 */
export function generateConnectionIntroPage(
  pageNumber: number,
  sectionNumber: string,
  /** 12原型の「つながりリード」（全社の状況を1文で述べたもの）。無ければ出さない。 */
  orgLead?: string
): string {
  const C = CONNECTION_INTRO_COPY;

  // 3つの型のミニ図。実際の描画ロジック（circlePath）と同じ形で描く。
  const shapeSvg = (shape: 'triangle' | 'vee' | 'spoke'): string => {
    const meta = CONSULT_SHAPE_META[shape];
    const W = 120;
    const H = 96;
    const cx = W / 2;
    const cy = H / 2 + 4;
    const R = 32;
    // 型ごとのキーパーソン配置（本編と同じ考え方: 単独=中心 / 複数=内周）
    const kpPts =
      shape === 'triangle'
        ? [
            { x: cx - 22, y: cy - 8 },
            { x: cx + 22, y: cy - 8 },
          ]
        : [{ x: cx, y: cy }];
    const others = [0, 1, 2, 3].map((i) => {
      const a = (2 * Math.PI * i) / 4 - Math.PI / 2;
      return { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
    });
    // スポークは相談が薄い＝線を1本だけにする
    const edges =
      shape === 'spoke'
        ? [{ from: others[0], to: kpPts[0] }]
        : others.map((o, i) => ({ from: o, to: kpPts[i % kpPts.length] }));
    const lines = edges
      .map(
        (e) =>
          `<line x1="${e.from.x.toFixed(1)}" y1="${e.from.y.toFixed(1)}" x2="${e.to.x.toFixed(1)}" y2="${e.to.y.toFixed(1)}" stroke="${meta.color}" stroke-width="1.4" stroke-opacity="0.8"/>`
      )
      .join('');
    const nodes = [
      ...others.map((o) =>
        circlePath(o.x, o.y, 6, 'fill="#EEF2F7" stroke="#AEBAC6" stroke-width="1"')
      ),
      ...kpPts.map((k) =>
        circlePath(k.x, k.y, 11, `fill="${meta.color}" stroke="${meta.color}" stroke-width="1.5"`)
      ),
    ].join('');
    return `<svg viewBox="0 0 ${W} ${H}" style="display:block;width:120px;height:96px;margin:0 auto;" preserveAspectRatio="xMidYMid meet">${lines}${nodes}</svg>`;
  };

  const shapeCards = C.shapes
    .map((sh) => {
      const meta = CONSULT_SHAPE_META[sh.shape];
      return `<div style="flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center;padding:0 1%;">
          ${shapeSvg(sh.shape)}
          <p style="margin:6px 0 4px;font-size:11px;font-weight:700;color:${meta.color};">${escapeHtml(sh.name)}</p>
          <p style="margin:0;font-size:10px;line-height:1.8;color:#444;">${escapeHtml(sh.text)}</p>
        </div>`;
    })
    .join('');

  const legendRows = C.legend
    .map(
      (l) =>
        `<li style="margin:0 0 5px;font-size:11px;line-height:1.75;list-style:none;">
           <span style="display:inline-block;min-width:104px;font-weight:700;color:#0066B7;">${escapeHtml(l.label)}</span>${escapeHtml(l.text)}
         </li>`
    )
    .join('');

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">つながりの構造とは</h3>
              <p class="c-report-single_header-team_hgroup_txt">${escapeHtml(C.subtitle)}</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;gap:2%;overflow:hidden;">
          ${orgLead ? `<p style="margin:0;padding:10px 14px;background:#EAF3FA;border-left:3px solid #0066B7;border-radius:4px;font-size:12px;line-height:1.9;">${escapeHtml(orgLead)}</p>` : ''}
          <p style="margin:0;font-size:12px;line-height:1.9;">${escapeHtml(C.lead)}</p>
          <ul style="margin:0;padding:10px 14px;background:#F5F7FA;border-radius:6px;">${legendRows}</ul>
          <div style="display:flex;gap:1.5%;align-items:flex-start;">${shapeCards}</div>
          <p style="margin:0;font-size:11.5px;line-height:1.9;color:#0066B7;font-weight:700;">${escapeHtml(C.closing)}</p>
        </div>
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** 部署別「つながりの構造」ページ（相談ネットワーク版）。2名未満はPDF除外。 */
export function generateConsultDeptPage(
  stat: ConsultDeptStat,
  pageNumber: number,
  sectionNumber: string
): string {
  const meta = CONSULT_SHAPE_META[stat.shape] ?? CONSULT_SHAPE_META.spoke;
  const tooFew = stat.members.length < MIN_NETWORK_MEMBERS;
  const body = tooFew
    ? `<div style="display:flex;align-items:center;justify-content:center;min-height:300px;">
         <p style="font-size:14px;line-height:2;color:#666;text-align:center;">
           回答者が${MIN_NETWORK_MEMBERS}名未満のため、つながりの構造を表示できません。<br>
           <span style="font-size:12px;">※このページは保存するPDFには含まれません。</span>
         </p>
       </div>`
    : `<div style="position:relative;width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;gap:2%;align-items:stretch;overflow:hidden;">
         <div style="flex:0 0 56%;min-width:0;display:flex;align-items:center;justify-content:center;">${generateConsultNetworkSvg(stat)}</div>
         <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
           <p style="font-size:12px;margin:0 0 10px;line-height:1.6;">
             <span style="display:inline-block;padding:4px 10px;border-radius:12px;background:${meta.color};color:#fff;font-size:11px;">${meta.mark} ${escapeHtml(meta.label)}</span>
           </p>
           <p style="font-size:12px;line-height:2;margin:0 0 10px;">${escapeHtml(stat.summary)}</p>
           <p style="font-size:11px;color:#666;line-height:1.9;margin:0;">
             メンバー ${stat.members.length}名／相談のつながり ${stat.edges.length}件<br>
             最大の被相談数 ${stat.maxInDegree}人
           </p>
         </div>
       </div>`;
  return `
    <li class="c-report-single_list_item _bg-white"${tooFew ? ' data-pdf-skip="true"' : ''}>
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">つながりの構造</h3>
              <p class="c-report-single_header-team_hgroup_txt">${escapeHtml(stat.dept)}</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        ${body}
        ${tooFew ? '' : noteBlock(ORG_REPORT_NOTES.connection)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** 組織ダイヤモンド（4軸レーダー）のSVG。Web版 DiamondChart と同じ配置（左上A/右上D/右下C/左下B）。 */
/** レポートの見出しで使っている青。軸ラベル・得点マーカーもこの色で統一する。 */
const REPORT_BLUE = '#0066B7';

/**
 * 組織ダイヤモンド（4軸レーダー）。Web版 DiamondChart と同じ配置（左上A/右上D/右下C/左下B）。
 *
 * 軸ラベルと得点マーカーは見出しと同じ青で統一し、ラベルは背景の座標円の外側に置く。
 * @param size 表示サイズ(px)。ページ内の枠に合わせて呼び出し側が指定する。
 */
export function generateOrgDiamondSvg(
  axis: { A: number; B: number; C: number; D: number },
  size = 360
): string {
  const MAXV = 12;
  const VB = 300; // viewBox
  const C = VB / 2; // 中心
  const R = 100; // 最大半径（座標円の外周）
  const LABEL_R = R + 30; // ラベルは座標円の外に出す
  const AX: Array<{ k: 'A' | 'B' | 'C' | 'D'; deg: number; label: string }> = [
    { k: 'A', deg: 135, label: 'A 論理' },
    { k: 'D', deg: 45, label: 'D 創造' },
    { k: 'C', deg: 315, label: 'C 共感' },
    { k: 'B', deg: 225, label: 'B 着実' },
  ];
  const at = (deg: number, r: number) => {
    const rad = (deg * Math.PI) / 180;
    return [C + r * Math.cos(rad), C - r * Math.sin(rad)] as const;
  };
  const pt = (deg: number, v: number) => at(deg, (Math.max(0, Math.min(MAXV, v)) / MAXV) * R);
  const poly = (['A', 'D', 'C', 'B'] as const)
    .map((k) => pt(AX.find((a) => a.k === k)!.deg, axis[k]).join(','))
    .join(' ');
  const rings = [0.25, 0.5, 0.75, 1]
    .map((f) => circlePath(C, C, R * f, 'fill="none" stroke="#D8E4EF" stroke-width="1"'))
    .join('');
  const spokes = AX.map((a) => {
    const [x, y] = at(a.deg, R);
    return `<line x1="${C}" y1="${C}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="#D8E4EF" stroke-width="1"/>`;
  }).join('');
  const dots = AX.map((a) => {
    const [x, y] = pt(a.deg, axis[a.k]);
    return circlePath(x, y, 4.5, `fill="${REPORT_BLUE}"`);
  }).join('');
  const labels = AX.map((a) => {
    const [x, y] = at(a.deg, LABEL_R);
    return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" fill="${REPORT_BLUE}" font-size="13" font-weight="700" text-anchor="middle" dominant-baseline="middle">${a.label} ${axis[a.k]}</text>`;
  }).join('');
  return `<svg viewBox="0 0 ${VB} ${VB}" style="display:block;width:${size}px;height:${size}px;max-width:100%;margin:0 auto;" preserveAspectRatio="xMidYMid meet">
      ${rings}${spokes}
      <polygon points="${poly}" fill="rgba(0,106,189,0.18)" stroke="${REPORT_BLUE}" stroke-width="2.5" stroke-linejoin="round"/>
      ${dots}${labels}
    </svg>`;
}

/** 散布図の1点＝1名。axis があれば本人のレーダー形（ミニ・ダイヤ）で描く。 */
export interface ScatterMember {
  x: number;
  y: number;
  /** 本人の4軸スコア(0〜12)。未指定なら小さな円で描く。 */
  axis?: { A: number; B: number; C: number; D: number };
}

/**
 * 「社員の分布」ページ。各点＝1名。
 *
 * 点は単なる丸ではなく、その人のレーダーチャートと同じ形（ミニ・ダイヤ）で描く。
 * 位置だけでなく「どんな形の人か」が一目で分かるようにするため。
 * 背景には組織ダイヤモンドと同じ同心円を敷き、中心からの距離感を掴めるようにする。
 */
export function generateScatterPage(
  scatter: ScatterMember[],
  lead: string,
  pageNumber: number,
  sectionNumber: string
): string {
  const W = 430;
  const H = 420;
  const CX = W / 2;
  const CY = H / 2;
  const RING_R = Math.min(W, H) / 2 - 26; // 背景の座標円の外周

  // 背景: 同心円 + 十字（組織ダイヤモンドと同じ見た目に揃える）
  const rings = [0.25, 0.5, 0.75, 1]
    .map((f) => circlePath(CX, CY, RING_R * f, 'fill="none" stroke="#D8E4EF" stroke-width="1"'))
    .join('');
  const cross =
    `<line x1="0" y1="${CY}" x2="${W}" y2="${CY}" stroke="#D8E4EF" stroke-width="1"/>` +
    `<line x1="${CX}" y1="0" x2="${CX}" y2="${H}" stroke="#D8E4EF" stroke-width="1"/>`;

  // 各メンバー: 本人の4軸でミニ・ダイヤを描く（左上A / 右上D / 右下C / 左下B）
  const MAXV = 12;
  const SIZE = 11; // ミニ・ダイヤの最大半径(px)
  const DEG = { A: 135, D: 45, C: 315, B: 225 } as const;
  const members = scatter
    .map((d) => {
      const px = Math.max(0, Math.min(1, d.x)) * W;
      const py = Math.max(0, Math.min(1, d.y)) * H;
      if (!d.axis) {
        return circlePath(
          px,
          py,
          4.5,
          `fill="rgba(0,106,189,0.55)" stroke="${REPORT_BLUE}" stroke-width="1"`
        );
      }
      const pts = (['A', 'D', 'C', 'B'] as const)
        .map((k) => {
          const rad = (DEG[k] * Math.PI) / 180;
          const r = (Math.max(0, Math.min(MAXV, d.axis![k])) / MAXV) * SIZE;
          return `${(px + r * Math.cos(rad)).toFixed(1)},${(py - r * Math.sin(rad)).toFixed(1)}`;
        })
        .join(' ');
      return `<polygon points="${pts}" fill="rgba(0,106,189,0.35)" stroke="${REPORT_BLUE}" stroke-width="1.2" stroke-linejoin="round"/>`;
    })
    .join('');

  // 四隅の軸ラベル（見出しと同じ青）
  const label = (x: number, y: number, anchor: string, text: string) =>
    `<text x="${x}" y="${y}" font-size="12" font-weight="700" fill="${REPORT_BLUE}" text-anchor="${anchor}">${text}</text>`;

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">社員の分布</h3>
              <p class="c-report-single_header-team_hgroup_txt">一人ひとりの思考タイプの広がり</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="position:relative;width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;gap:2%;align-items:stretch;overflow:hidden;">
          <div style="flex:0 0 56%;min-width:0;display:flex;align-items:center;justify-content:center;">
            <svg viewBox="0 0 ${W} ${H}" style="display:block;width:450px;height:440px;max-width:100%;margin:0 auto;" preserveAspectRatio="xMidYMid meet">
              <rect x="0.5" y="0.5" width="${W - 1}" height="${H - 1}" rx="8" fill="#FAFBFC" stroke="#E7EAEE" stroke-width="1"/>
              ${rings}${cross}
              ${label(14, 20, 'start', 'A 論理')}
              ${label(W - 14, 20, 'end', 'D 創造')}
              ${label(W - 14, H - 10, 'end', 'C 共感')}
              ${label(14, H - 10, 'start', 'B 着実')}
              ${members}
            </svg>
          </div>
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
            <p style="font-size:12px;line-height:2;">${escapeHtml(lead)}</p>
            <p style="font-size:11px;color:#888;margin-top:10px;">※ ひとつの形が1名を表します（合計 ${scatter.length} 名）。形はその人のレーダーチャートと同じです。</p>
          </div>
        </div>
        ${noteBlock(ORG_REPORT_NOTES.distribution)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** 「評価を上げるために」ページ。現在→目標と改善提案。 */
export function generateImprovementPage(
  evalData: OrgEvaluationInput,
  pageNumber: number,
  sectionNumber: string
): string {
  const items = (evalData.improvements ?? [])
    .map(
      (imp) => `
            <div style="display:flex;gap:10px;margin-bottom:12px;">
              <span style="flex:0 0 24px;height:24px;border-radius:12px;background:${escapeHtml(imp.rankColor)};color:#fff;font-size:12px;display:flex;align-items:center;justify-content:center;">${imp.rank}</span>
              <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
                <p style="font-size:13px;font-weight:700;margin:0 0 3px;">${escapeHtml(imp.title)}</p>
                <p style="font-size:11px;color:#555;line-height:1.8;margin:0 0 3px;">${escapeHtml(imp.reason)}</p>
                <p style="font-size:11px;line-height:1.8;margin:0;"><span style="color:#0066B7;">取り組み：</span>${escapeHtml(imp.action)}</p>
                <p style="font-size:11px;line-height:1.8;margin:0;"><span style="color:#2D8A4E;">期待効果：</span>${escapeHtml(imp.effect)}</p>
              </div>
            </div>`
    )
    .join('');
  const target = evalData.improvementTarget ?? 0;
  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">評価を上げるために</h3>
              <p class="c-report-single_header-team_hgroup_txt">次に取り組むこと</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;overflow:hidden;">
          <p style="font-size:12px;line-height:2;margin:0 0 12px;">${escapeHtml(evalData.improvementLead ?? '')}</p>
          <div style="display:flex;align-items:center;gap:14px;margin-bottom:16px;">
            <div style="text-align:center;"><p style="font-size:10px;color:#666;margin:0;">現在</p><p style="font-size:26px;font-weight:700;color:#0066B7;margin:0;">${evalData.totalScore}</p></div>
            <span style="font-size:18px;color:#999;">→</span>
            <div style="text-align:center;"><p style="font-size:10px;color:#666;margin:0;">目標</p><p style="font-size:26px;font-weight:700;color:#2D8A4E;margin:0;">${target}</p></div>
            <span style="flex:1;height:10px;background:#EEF1F4;border-radius:5px;position:relative;display:inline-block;">
              <span style="display:block;height:100%;width:${Math.max(0, Math.min(100, evalData.totalScore))}%;background:#0066B7;border-radius:5px;"></span>
            </span>
          </div>
          ${items}
        </div>
        ${noteBlock(ORG_REPORT_NOTES.improvement)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** 「組織の評価①: 組織スコア」ページ。Web版と同じ値（orgScore.ts）を表示する。 */
export function generateOrgScorePage(
  evalData: OrgEvaluationInput,
  pageNumber: number,
  sectionNumber: string
): string {
  const gaugeRows = evalData.gauges
    .map(
      (g) => `
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
              <span style="width:110px;font-size:12px;">${escapeHtml(g.label)}</span>
              <span style="flex:1;height:10px;background:#EEF1F4;border-radius:5px;overflow:hidden;display:inline-block;">
                <span style="display:block;height:100%;width:${Math.max(0, Math.min(100, g.score))}%;background:#0066B7;"></span>
              </span>
              <span style="width:64px;text-align:right;font-size:12px;">${g.score}点 (${escapeHtml(g.grade)})</span>
            </div>`
    )
    .join('');

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">組織の評価</h3>
              <p class="c-report-single_header-team_hgroup_txt">組織スコア</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="position:relative;width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;gap:2%;align-items:stretch;overflow:hidden;">
          <div style="flex:0 0 26%;min-width:0;text-align:center;background:#F4F8FC;border-radius:10px;padding:3% 1.5%;box-sizing:border-box;align-self:center;">
            <p style="font-size:11px;color:#666;margin:0 0 6px;">総合評価</p>
            <p style="font-size:46px;font-weight:700;color:#0066B7;line-height:1.1;margin:0;">${evalData.totalScore}<span style="font-size:14px;font-weight:400;">点</span></p>
            <p style="font-size:13px;margin:6px 0 0;">評価 ${escapeHtml(evalData.totalGrade)}</p>
          </div>
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
            ${gaugeRows}
            <p style="font-size:12px;line-height:1.9;margin-top:14px;">${escapeHtml(evalData.evaluationLead)}</p>
          </div>
        </div>
        ${noteBlock(ORG_REPORT_NOTES.orgScore)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** 「組織の評価②: 組織タイプ」ページ。 */
export function generateOrgTypePage(
  evalData: OrgEvaluationInput,
  pageNumber: number,
  sectionNumber: string
): string {
  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">組織の評価</h3>
              <p class="c-report-single_header-team_hgroup_txt">組織タイプ</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="position:relative;width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;gap:2%;align-items:stretch;overflow:hidden;">
          <div style="flex:0 0 48%;min-width:0;text-align:center;display:flex;flex-direction:column;justify-content:center;">
            <p style="font-size:11px;color:#666;margin:0 0 4px;">組織タイプ</p>
            <p style="font-size:20px;font-weight:700;color:#0066B7;line-height:1.4;margin:0 0 4px;">${escapeHtml(evalData.orgTypeName || '—')}</p>
            ${evalData.orgTypeCode ? `<p style="font-size:11px;color:#888;margin:0 0 8px;">コード: ${escapeHtml(evalData.orgTypeCode)}</p>` : ''}
            ${evalData.axis ? `<div style="flex:1;min-height:0;display:flex;align-items:center;justify-content:center;">${generateOrgDiamondSvg(evalData.axis)}</div>` : ''}
          </div>
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
            <p style="font-size:12px;line-height:2;">${escapeHtml(evalData.orgTypeDesc || '※ 組織タイプの解説文は調整中です。')}</p>
          </div>
        </div>
        ${noteBlock(ORG_REPORT_NOTES.orgType)}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

export function generateSectionTitlePage(num: number, title: string, pageNumber: number): string {
  return `
    <li class="c-report-single_list_item">
      <div class="c-report-single_title">
        <div class="c-report-single_title_inner">
          <hgroup class="c-report-single_title_hgroup">
            <p class="c-report-single_title_hgroup_num">${num}</p>
            <h3 class="c-report-single_title_hgroup_ttl">${escapeHtml(title)}</h3>
          </hgroup>
        </div>
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

export function generateDepartmentNetworkPage(
  deptName: string,
  members: MemberScore[],
  pageNumber: number,
  sectionNumber: string,
  options: NetworkGraphOptions = {}
): string {
  if (members.length < MIN_NETWORK_MEMBERS) {
    return `
    <li class="c-report-single_list_item _bg-white" data-pdf-skip="true">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">つながりの構造</h3>
              <p class="c-report-single_header-team_hgroup_txt">${escapeHtml(deptName)}</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div class="c-report-single_network_inner">
          <div class="c-report-single_network_main">
            <div class="c-report-single_network_main_inner" style="display:flex;align-items:center;justify-content:center;min-height:360px;">
              <p style="font-size:14px;line-height:2;color:#666;text-align:center;">
                回答者が${MIN_NETWORK_MEMBERS}名未満のため、つながりの構造を表示できません。<br>
                <span style="font-size:12px;">※このページは保存するPDFには含まれません。</span>
              </p>
            </div>
          </div>
        </div>
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
  }
  // 2名以上: 通常のネットワーク図ページを部署名付きで生成する。
  return generateNetworkPage(members, pageNumber, sectionNumber, options, undefined, deptName);
}

export function generateNetworkPage(
  members: MemberScore[],
  pageNumber: number,
  sectionNumber: string,
  options: NetworkGraphOptions = {},
  adjustedNodePositions?: { [id: number]: { x: number; y: number } },
  /** 部署別ページのときの部署名（未指定なら全社ページとして従来の見出しを使う） */
  deptName?: string
): string {
  // 人数が多いときはノード(レーダーチャート)を縮小して重なり・見切れを緩和する。
  // 少人数のときは従来どおり 12px。generateNetworkGraph と generateNetworkSvg に同じ値を渡す。
  const nodeRadius = computeNetworkNodeRadius(members.length);

  // Generate network graph SVG with options - larger size for better spacing
  const graphData = generateNetworkGraph(members, 600, 480, options, nodeRadius);

  // Apply adjusted positions if provided
  if (adjustedNodePositions) {
    graphData.nodes = graphData.nodes.map((node) => {
      const adjusted = adjustedNodePositions[node.id];
      if (adjusted) {
        return { ...node, x: adjusted.x, y: adjusted.y };
      }
      return node;
    });
  }

  const networkSvg = generateNetworkSvg(graphData, 600, 480, nodeRadius);

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${sectionNumber}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">つながりの構造</h3>
              <p class="c-report-single_header-team_hgroup_txt">${deptName ? escapeHtml(deptName) : '全社'}</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>

        <div class="c-report-single_network_inner">
          <div class="c-report-single_network_main">
            <div class="c-report-single_network_main_inner">
              <figure class="c-report-single_network_main_img">
                ${networkSvg}
              </figure>
            </div>
          </div>
          <div class="c-report-single_network_sub">
            <p class="c-report-single_network_sub_num _prev-none">ネットワーク図</p>
            <dl class="c-report-single_network_sub_dl">
              <div class="c-report-single_network_sub_dl_item">
                <dt class="c-report-single_network_sub_dl_theme">
                  GBタイプの類似度に基づく関係性
                </dt>
                <dd class="c-report-single_network_sub_dl_des">
                  各メンバーのレーダーチャートをノードとし、GBタイプの類似度が高いメンバー同士を線で結んでいます。太い線ほど類似度が高く、共鳴しやすい関係を示します。
                </dd>
              </div>
              <div class="c-report-single_network_sub_dl_item">
                <dt class="c-report-single_network_sub_dl_theme">
                  類似度の算出方法
                </dt>
                <dd class="c-report-single_network_sub_dl_des">
                  4要素（A論理・B秩序・C共感・D創造）の値を2x2行列として扱い、行列間の距離を計算。さらに「強み要素」の一致度で補正を加えています。上位50%の関係性のみ表示しています。
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div class="c-report-single_network_description">
          <div class="c-report-single_network_description_box">
            <p class="c-report-single_network_description_box_txt _sync">共鳴しやすい関係<span class="c-report-single_network_description_box_txt_sml">(太いほど共鳴しやすい)</span></p>
          </div>

          <figure class="c-report-single_network_description_type">
            <p class="c-report-single_network_description_type_img"><img src="/report-template/assets/img/description_img_type_01.svg" alt=""></p>
            <figcaption class="c-report-single_network_description_type_cap">
              <p class="c-report-single_network_description_type_cap_txt">
                タイプが同一の関係は<br>
                気は合うが視野が狭くなる
              </p>
              <p class="c-report-single_network_description_type_cap_compatibility">
                相性
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
              </p>
            </figcaption>
          </figure>

          <figure class="c-report-single_network_description_type">
            <p class="c-report-single_network_description_type_img"><img src="/report-template/assets/img/description_img_type_02.svg" alt=""></p>
            <figcaption class="c-report-single_network_description_type_cap">
              <p class="c-report-single_network_description_type_cap_txt">
                タイプが上下の関係は<br>
                協力しやすいが理解が鍵
              </p>
              <p class="c-report-single_network_description_type_cap_compatibility">
                相性
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
              </p>
            </figcaption>
          </figure>

          <figure class="c-report-single_network_description_type">
            <p class="c-report-single_network_description_type_img"><img src="/report-template/assets/img/description_img_type_03.svg" alt=""></p>
            <figcaption class="c-report-single_network_description_type_cap">
              <p class="c-report-single_network_description_type_cap_txt">
                タイプが左右の関係は<br>
                強みを補うが噛み合いにくい
              </p>
              <p class="c-report-single_network_description_type_cap_compatibility">
                相性
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
              </p>
            </figcaption>
          </figure>

          <figure class="c-report-single_network_description_type">
            <p class="c-report-single_network_description_type_img"><img src="/report-template/assets/img/description_img_type_04.svg" alt=""></p>
            <figcaption class="c-report-single_network_description_type_cap">
              <p class="c-report-single_network_description_type_cap_txt">
                タイプが対角の関係は<br>
                補完的だが衝突しやすい
              </p>
              <p class="c-report-single_network_description_type_cap_compatibility">
                相性
                <svg class="c-report-single_network_description_type_cap_compatibility_icon" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
                <svg class="c-report-single_network_description_type_cap_compatibility_icon _gray" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 0L9.20001 4.30029L14 5L10.5 8.5L11.3 13.3003L7 11L2.70001 13.3003L3.5 8.5L0 5L4.80005 4.30029L7 0Z"/></svg>
              </p>
            </figcaption>
          </figure>
        </div>

        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/**
 * Calculate average scores for team
 */
function calculateAverageScores(members: MemberScore[]): {
  A: number;
  B: number;
  C: number;
  D: number;
} {
  if (members.length === 0) {
    return { A: 0, B: 0, C: 0, D: 0 };
  }

  const sum = members.reduce(
    (acc, m) => ({
      A: acc.A + m.scores.A,
      B: acc.B + m.scores.B,
      C: acc.C + m.scores.C,
      D: acc.D + m.scores.D,
    }),
    { A: 0, B: 0, C: 0, D: 0 }
  );

  return {
    A: Math.round((sum.A / members.length) * 10) / 10,
    B: Math.round((sum.B / members.length) * 10) / 10,
    C: Math.round((sum.C / members.length) * 10) / 10,
    D: Math.round((sum.D / members.length) * 10) / 10,
  };
}

/**
 * Generate point page (Requirement 47-48)
 * 組織がより輝くためのポイント
 */
function generatePointPage(
  members: MemberScore[],
  pageNumber: number,
  teamDesc2x: TeamDescriptions2x,
  distributionCounts: TeamDistributionCounts
): string {
  const { cntA, cntB, cntC, cntD } = distributionCounts;
  const total = cntA + cntB + cntC + cntD;

  // Calculate TSCORE values: cntX / (cntA+cntB+cntC+cntD) * 24
  const tscoreA = total > 0 ? Math.round((cntA / total) * 24 * 10) / 10 : 0;
  const tscoreB = total > 0 ? Math.round((cntB / total) * 24 * 10) / 10 : 0;
  const tscoreC = total > 0 ? Math.round((cntC / total) * 24 * 10) / 10 : 0;
  const tscoreD = total > 0 ? Math.round((cntD / total) * 24 * 10) / 10 : 0;

  console.log('=== TSCORE Calculation ===');
  console.log('cntA:', cntA, 'cntB:', cntB, 'cntC:', cntC, 'cntD:', cntD);
  console.log('total:', total);
  console.log('TSCOREA:', tscoreA, 'TSCOREB:', tscoreB, 'TSCOREC:', tscoreC, 'TSCORED:', tscoreD);
  console.log('==========================');

  const teamScores = { A: tscoreA, B: tscoreB, C: tscoreC, D: tscoreD };
  const path = calculateRadarPath(teamScores);

  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_point">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">3</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">組織がより輝くためのポイント</h3>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>

        <div class="c-report-single_point_inner">
          <div class="c-report-single_point_box">
            <h4 class="c-report-single_point_box_ttl">同業種との組織のGBタイプ分布比較</h4>
            <div class="c-report-single_point_box_radar">
              <!-- Team distribution radar chart (TSCORE values) -->
              <svg class="js-radar-chart" data-a="${tscoreA}" data-b="${tscoreB}" data-c="${tscoreC}" data-d="${tscoreD}" viewBox="0 0 150 150">
                <path class="js-radar-chart_line" d="${path}"></path>
              </svg>
              <!-- Industry average radar chart -->
              <svg class="js-radar-chart" data-a="6.3" data-b="4.4" data-c="5.7" data-d="7.6" viewBox="0 0 150 150">
                <path class="js-radar-chart_line _compare-color" d="${calculateRadarPath({ A: 6.3, B: 4.4, C: 5.7, D: 7.6 })}"></path>
              </svg>

              <p class="c-report-single_point_box_radar_txt _a">
                <span class="c-report-single_point_box_radar_txt_lg">A</span>論理
              </p>
              <p class="c-report-single_point_box_radar_txt _b">
                <span class="c-report-single_point_box_radar_txt_lg">B</span>秩序
              </p>
              <p class="c-report-single_point_box_radar_txt _c">
                <span class="c-report-single_point_box_radar_txt_lg">C</span>共感
              </p>
              <p class="c-report-single_point_box_radar_txt _d">
                <span class="c-report-single_point_box_radar_txt_lg">D</span>創造
              </p>
            </div>
            <p class="c-report-single_point_box_note">
              <span class="c-report-single_point_box_note_txt _you">あなたのチャート</span>
              <span class="c-report-single_point_box_note_txt _compare">同業種のチャート平均</span>
            </p>
          </div>

          <dl class="c-report-single_point_dl">
            <div class="c-report-single_point_dl_item">
              <dt class="c-report-single_point_dl_point">
                <p class="c-report-single_point_dl_point_txt _utilize">強みを生かすポイント</p>
                <p class="c-report-single_point_dl_point_box">
                  <span class="c-report-single_point_dl_point_box_txt">共感</span>
                  <span class="c-report-single_point_dl_point_box_txt">創造</span>
                </p>
              </dt>
              <dt class="c-report-single_point_dl_theme">${escapeHtml(teamDesc2x.tdesc21)}</dt>
              <dd class="c-report-single_point_dl_des">${escapeHtml(teamDesc2x.tdesc22)}</dd>
            </div>
            <div class="c-report-single_point_dl_item">
              <dt class="c-report-single_point_dl_point">
                <p class="c-report-single_point_dl_point_txt _overcome">弱みを克服するポイント</p>
                <p class="c-report-single_point_dl_point_box">
                  <span class="c-report-single_point_dl_point_box_txt">秩序</span>
                </p>
              </dt>
              <dt class="c-report-single_point_dl_theme">${escapeHtml(teamDesc2x.tdesc23)}</dt>
              <dd class="c-report-single_point_dl_des">${escapeHtml(teamDesc2x.tdesc24)}</dd>
            </div>
            <div class="c-report-single_point_dl_item">
              <dt class="c-report-single_point_dl_point">
                <p class="c-report-single_point_dl_point_txt _performance">パフォーマンス向上ポイント</p>
                <p class="c-report-single_point_dl_point_box">
                  <span class="c-report-single_point_dl_point_box_txt">論理</span>
                  <span class="c-report-single_point_dl_point_box_txt">秩序</span>
                  <span class="c-report-single_point_dl_point_box_txt">共感</span>
                  <span class="c-report-single_point_dl_point_box_txt">創造</span>
                </p>
              </dt>
              <dt class="c-report-single_point_dl_theme">${escapeHtml(teamDesc2x.tdesc25)}</dt>
              <dd class="c-report-single_point_dl_des">${escapeHtml(teamDesc2x.tdesc26)}</dd>
            </div>
          </dl>

          <hgroup class="c-report-single_point_hgroup">
            <p class="c-report-single_point_hgroup_txt">あなたの組織がより輝くためには</p>
            <h4 class="c-report-single_point_hgroup_ttl">${escapeHtml(teamDesc2x.tdesc27)}</h4>
          </hgroup>
        </div>

        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/**
 * Generate team report HTML
 */
export async function generateTeamReportHtml(data: TeamReportData): Promise<string> {
  const { companyName, date, members, networkOptions = {} } = data;

  // Calculate number of pages needed for radar charts
  const pageCount = Math.ceil(members.length / MEMBERS_PER_PAGE);

  // Calculate team type counts (11-19)
  const counts = calculateTeamTypeCounts(members);

  // Debug: Output counts to console for verification
  console.log('=== Team Type Counts ===');
  console.log('CCCNT:', counts.CCCNT, '(All 4 elements <= 14)');
  console.log('RCCNT:', counts.RCCNT, '(XVALUE+ & C>D)');
  console.log('RDCNT:', counts.RDCNT, '(XVALUE+ & C<D)');
  console.log('LACNT:', counts.LACNT, '(XVALUE- & A>B)');
  console.log('LBCNT:', counts.LBCNT, '(XVALUE- & A<B)');
  console.log('TACNT:', counts.TACNT, '(YVALUE- & A>D)');
  console.log('TDCNT:', counts.TDCNT, '(YVALUE- & A<D)');
  console.log('BBCNT:', counts.BBCNT, '(YVALUE+ & B>C)');
  console.log('BCCNT:', counts.BCCNT, '(YVALUE+ & B<C)');
  console.log('========================');

  // Determine TTYPE (20-22)
  const ttype = determineTType(counts);
  console.log('TTYPE:', ttype);

  // Find matching team judgment pattern (23)
  // name=コード(RC/RD/LA/LB/BB/BC/TA/TD/CC/EE) で登録されるため findPatternByCode で引く。
  const pattern = await findPatternByCode(ttype);

  // パターン未登録なら「分析中」プレースホルダのまま出力せず、明確に中断する。
  if (!pattern) {
    throw new Error(
      `組織診断の判定パターン（コード: ${ttype}）が未登録のため、分析結果を出力できません。` +
        `管理画面「チーム判定パターン」でパターンを登録してから再度お試しください。`
    );
  }

  // Get team descriptions (24)
  const teamDescriptions = getTeamDescriptions(pattern);

  // ===== ページ採番（DOM に並ぶ順で一括決定する） =====
  // 表紙(番号なし) / 目次=01 / [1 組織の評価] 中扉・スコア・タイプ・社員の分布 /
  // [2 つながりの構造] 中扉・部署別 / [3 評価を上げるために] 中扉・本文 /
  // [4 組織メンバーの各タイプ] 中扉・レーダー一覧(21名/ページ)
  // ここで採番をまとめないと、生成順と DOM 順の食い違いで番号が重複・逆転する。
  const useConsult = (data.consultDepts?.length ?? 0) > 0;
  const deptCount = useConsult ? (data.consultDepts ?? []).length : (data.departments ?? []).length;
  let pno = 1; // 目次=01
  const noSecOrgEval = ++pno;
  const noOrgScore = ++pno;
  const noOrgType = ++pno;
  const noDistribution = ++pno; // 社員の分布（scatter 版 / overview 版 共通）
  const noSecConnection = ++pno;
  // 「つながりの構造とは」解説ページ（相談ネットワークがあるときだけ出す）
  const noConnectionIntro = useConsult ? ++pno : 0;
  const noNetwork = useConsult ? 0 : ++pno; // 相談ネットワークが無いときの全社図
  const deptNos = Array.from({ length: deptCount }, () => ++pno);
  const noSecImprove = ++pno;
  const noImprove = ++pno; // 評価を上げるために（improvement 版 / point 版 共通）
  const noSecMembers = ++pno;
  const radarNos = Array.from({ length: pageCount }, () => ++pno);

  // Generate overview page (distribution chart)
  const overviewPage = generateOverviewPage(
    members,
    noDistribution,
    teamDescriptions,
    noDistribution,
    '1-3'
  );

  // Generate network graph page (Requirements 25-32, 37-38)
  const networkPage = generateNetworkPage(
    members,
    noNetwork,
    '2-1',
    networkOptions,
    data.adjustedNodePositions
  );

  const secOrgEval = generateSectionTitlePage(1, '組織の評価', noSecOrgEval);
  const orgScorePage = data.orgEvaluation
    ? generateOrgScorePage(data.orgEvaluation, noOrgScore, '1-1')
    : '';
  const orgTypePage = data.orgEvaluation
    ? generateOrgTypePage(data.orgEvaluation, noOrgType, '1-2')
    : '';
  // 社員の分布（Web版 §02 相当）。scatter があれば新ページ、無ければ既存 overviewPage を使う。
  const scatterPage = data.orgEvaluation?.scatter?.length
    ? generateScatterPage(
        data.orgEvaluation.scatter,
        data.orgEvaluation.distributionLead ?? '',
        noDistribution,
        '1-3'
      )
    : '';
  const secConnection = generateSectionTitlePage(2, 'つながりの構造', noSecConnection);
  const connectionIntroPage = useConsult
    ? generateConnectionIntroPage(noConnectionIntro, '2-1', data.connectionLead)
    : '';
  const secImprove = generateSectionTitlePage(3, '評価を上げるために', noSecImprove);
  // 評価を上げるために（Web版 §04 相当）。improvements があれば新ページを使う。
  const improvementPage = data.orgEvaluation?.improvements?.length
    ? generateImprovementPage(data.orgEvaluation, noImprove, '3-1')
    : '';
  const secMembers = generateSectionTitlePage(4, '組織メンバーの各タイプ', noSecMembers);

  // 組織メンバーの各タイプ（レーダー一覧）。節番号は 4-n、フッタは通し番号。
  const radarPages: string[] = [];
  for (let i = 0; i < pageCount; i++) {
    const startIdx = i * MEMBERS_PER_PAGE;
    const endIdx = Math.min(startIdx + MEMBERS_PER_PAGE, members.length);
    radarPages.push(
      generateRadarPage(
        members.slice(startIdx, endIdx),
        i + 1,
        companyName,
        radarNos[i],
        `4-${i + 1}`
      )
    );
  }

  // 部署別「つながりの構造」ページ（departments 指定時のみ）。
  // 2名未満は generateDepartmentNetworkPage 側で理由表示＋PDF除外になる。
  const departmentPages: string[] = useConsult
    ? (data.consultDepts ?? []).map((stat, i) =>
        generateConsultDeptPage(stat, deptNos[i], `2-${i + 2}`)
      )
    : (data.departments ?? []).map((d, i) =>
        generateDepartmentNetworkPage(d.name, d.members, deptNos[i], `2-${i + 1}`, networkOptions)
      );

  // 目次（Requirement 39）。本文と同じ採番から作るのでページ番号がずれない。
  const deptNames = useConsult
    ? (data.consultDepts ?? []).map((d) => d.dept)
    : (data.departments ?? []).map((d) => d.name);
  const tocPage = generateTocPage([
    {
      title: '組織の評価',
      entries: [
        ...(data.orgEvaluation ? [{ no: '1-1', title: '組織スコア', page: noOrgScore }] : []),
        ...(data.orgEvaluation ? [{ no: '1-2', title: '組織タイプ', page: noOrgType }] : []),
        {
          no: '1-3',
          title: '社員の分布',
          page: noDistribution,
          note: scatterPage ? undefined : teamDescriptions.tdesc19,
        },
      ],
    },
    {
      title: 'つながりの構造',
      entries: [
        ...(useConsult
          ? [{ no: '2-1', title: 'つながりの構造とは', page: noConnectionIntro }]
          : [{ no: '2-1', title: 'メンバー間の関係性', page: noNetwork }]),
        ...deptNames.map((name, i) => ({ no: `2-${i + 2}`, title: name, page: deptNos[i] })),
      ],
    },
    {
      title: '評価を上げるために',
      entries: [
        {
          no: '3-1',
          title: improvementPage ? '次に取り組むこと' : '同業種との比較と改善ポイント',
          page: noImprove,
        },
      ],
    },
    {
      title: '組織メンバーの各タイプ',
      entries: radarNos.map((n, i) => ({
        no: `4-${i + 1}`,
        title:
          pageCount > 1 ? `レーダーチャート一覧（${i + 1}/${pageCount}）` : 'レーダーチャート一覧',
        page: n,
      })),
    },
  ]);

  // Calculate team distribution for point page (Step 1-2)
  const distributionCounts = calculateTeamDistribution(members);

  // Judge distribution ratio (Step 3)
  const distributionJudgment = judgeDistributionRatio(distributionCounts);

  // Step 4-5: Get BTTYPE pattern for tdesc21, tdesc22, tdesc27
  let btPattern: TeamJudgmentPattern | null = null;
  if (distributionJudgment.bttype) {
    btPattern = await findPatternByCode(distributionJudgment.bttype);
  }

  // Step 6-7: Get STTYPE pattern for tdesc23-26
  let stPattern: TeamJudgmentPattern | null = null;
  if (distributionJudgment.sttype) {
    stPattern = await findPatternByCode(distributionJudgment.sttype);
  }

  // Build teamDesc2x from both patterns
  const teamDesc2x: TeamDescriptions2x = {
    // From BTTYPE pattern (Step 5)。未取得時は主 pattern → 既定文言の順でフォールバック。
    tdesc21: btPattern?.tdesc21 || pattern?.tdesc21 || '強みを活かすテーマ',
    tdesc22: btPattern?.tdesc22 || pattern?.tdesc22 || '強みを活かすための具体的なアドバイス',
    tdesc27: btPattern?.tdesc27 || pattern?.tdesc27 || '組織がより輝くためのメッセージ',
    // From STTYPE pattern (Step 7)。未取得時は主 pattern → 既定文言の順でフォールバック。
    tdesc23: stPattern?.tdesc23 || pattern?.tdesc23 || '弱みを克服するテーマ',
    tdesc24: stPattern?.tdesc24 || pattern?.tdesc24 || '弱みを克服するための具体的なアドバイス',
    tdesc25: stPattern?.tdesc25 || pattern?.tdesc25 || 'パフォーマンス向上テーマ',
    tdesc26:
      stPattern?.tdesc26 || pattern?.tdesc26 || 'パフォーマンス向上のための具体的なアドバイス',
  };

  console.log('=== Point Page Data ===');
  console.log('BTTYPE:', distributionJudgment.bttype);
  console.log('STTYPE:', distributionJudgment.sttype);
  console.log('teamDesc2x:', teamDesc2x);
  console.log('=======================');

  // Generate point page (Requirement 47-48) - use distributionCounts for TSCORE calculation
  const pointPageNumber = noImprove;
  const pointPage = generatePointPage(members, pointPageNumber, teamDesc2x, distributionCounts);

  // Build the complete HTML
  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta name="format-detection" content="telephone=no">
  <title>${escapeHtml(date)} 組織診断結果</title>
  <link rel="stylesheet" href="/report-template/assets/css/style.css" media="all">
  <script src="/report-template/assets/js/main.js" defer></script>
  <style>
    /*
     * 印刷（PDF保存）専用スタイル
     * - セクションごとにA4横1ページで区切る
     * - 全ページを包む白いカードパネル（後ろの白背景）を除去する
     * ※画面表示（別タブ）には影響しない
     */
    @media print {
      @page {
        size: A4 landscape;
        margin: 0;
      }

      /* グラデーション・レーダー色などの背景色を印刷に反映 */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* 左上のラベルタブは印刷では不要 */
      .c-report-single_label {
        display: none !important;
      }

      /* ラッパー類の余白・幅をリセット（レイアウト崩れ防止） */
      html,
      body,
      .l-all-wrap,
      .l-main,
      .c-sec-report,
      .c-sec-report > .inner,
      .c-report-single {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        max-width: none !important;
        background: transparent !important;
      }

      /* 後ろの白いカードパネルを除去（角丸・余白・白背景を消す） */
      .c-report-single_inner {
        margin: 0 !important;
        padding: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
      }

      /* リストは各ページを縦積みし、左上基準に配置（拡大の原点を合わせる） */
      .c-report-single_list._team {
        margin: 0 !important;
        width: 950px !important;
      }

      /*
       * 1セクション = A4横1ページ。
       * コンテンツは950x670pxで作られており、A4横(≒1122x793px)より小さいため
       * 左上基準で約1.18倍に拡大してページ全面に広げる（余白ゼロ）。
       * 縦横比がほぼ同じ(1.418 vs 1.414)なので歪みは出ない。
       */
      .c-report-single_list._team .c-report-single_list_item {
        margin: 0 !important;
        width: 950px !important;
        height: 670px !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        overflow: visible !important;
        transform: scale(var(--print-fit, 1.181));
        transform-origin: top left;
        page-break-after: always;
        -webkit-page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      /* 最終ページの後ろに空白ページを作らない */
      .c-report-single_list._team .c-report-single_list_item:last-child {
        page-break-after: avoid;
        break-after: avoid;
      }
    }
  </style>
</head>
<body>
<div class="l-all-wrap">
  <main class="l-main">
    <section class="c-sec-report">
      <div class="inner">
        <div class="c-report-single">
          <h2 class="c-report-single_label _team">${escapeHtml(date)} 組織診断結果</h2>
          <div class="c-report-single_inner">
            <ul class="c-report-single_list _team">
              <!-- Cover Page -->
              <li class="c-report-single_list_item _bg-white">
                <div class="c-report-single_organization">
                  <hgroup class="c-report-single_organization_hgroup">
                    <p class="c-report-single_organization_hgroup_company">${escapeHtml(companyName)} 御中</p>
                    <h3 class="c-report-single_organization_hgroup_ttl">
                      <span class="c-report-single_organization_hgroup_ttl_img"><img src="/report-template/assets/img/organization_ttl.svg" alt="組織レポート"></span>
                      <span class="c-report-single_organization_hgroup_ttl_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></span>
                    </h3>
                    <p class="c-report-single_organization_hgroup_date">${escapeHtml(date)}</p>
                  </hgroup>
                  <picture class="c-report-single_organization_img">
                    <img src="/report-template/assets/img/organization_img.png" alt="">
                  </picture>
                </div>
              </li>

              <!-- Table of Contents Page (Requirement 39) -->
              ${tocPage}

              <!-- ===== 1. 組織の評価 ===== -->
              ${secOrgEval}
              ${orgScorePage}
              ${orgTypePage}

              <!-- 社員の分布（Web版§02） -->
              ${scatterPage || overviewPage}

              <!-- ===== 2. つながりの構造 ===== -->
              ${secConnection}

              <!-- つながりの構造とは（記号と3つの型の解説） -->
              ${connectionIntroPage}

              <!-- 全社のネットワーク図（相談ネットワークが無い場合のフォールバック） -->
              ${useConsult ? '' : networkPage}

              <!-- 部署別ページ（2名未満は data-pdf-skip でPDF除外） -->
              ${departmentPages.join('\n')}

              <!-- ===== 3. 評価を上げるために ===== -->
              ${secImprove}
              ${improvementPage || pointPage}

              <!-- ===== 4. 組織メンバーの各タイプ ===== -->
              ${secMembers}
              ${radarPages.join('\n')}
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>
</div>
</body>
</html>`;

  return html;
}

/**
 * Open report in a new tab/window
 */
export function openTeamReportWindow(html: string, title: string): void {
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
    newWindow.document.title = title;
  } else {
    alert('新しいウィンドウを開けませんでした。ブラウザの設定を確認してください。');
  }
}

/**
 * Download team report as PDF (Requirement 45)
 * @param html HTML content
 * @param filename Output filename
 */
export async function downloadTeamReportPdf(html: string, filename: string): Promise<void> {
  const { generateTeamReportPdf } = await import('./pdfGenerator');
  await generateTeamReportPdf(html, filename);
}

/**
 * Escape HTML special characters
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
